import format from 'date-fns-tz/format';
import formatInTimeZone from 'date-fns-tz/formatInTimeZone';
import getTimezoneOffset from 'date-fns-tz/getTimezoneOffset';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addWeeks from 'date-fns/addWeeks';
import formatISO from 'date-fns/formatISO';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import subWeeks from 'date-fns/subWeeks';

import { type DatePartition } from '../../common/types';

const HOUR_MILLIS = 60 * 60 * 1000;
const MINUTE_MILLIS = 60 * 1000;
const HOUR_MINUTES = 60;

/**
 * @return timeZone - 'Europe/Istanbul' | 'Africa/Casablanca' | ...
 */
export const guessTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Get offset difference between browser and remote timezone
 * @example Browser: UTC +3:00, Remote: UTC +1:00 => 2 Hours (7200000)
 * @example Browser: UTC +3:00, Remote: UTC -9:00 => 12 Hours (43200000)
 * @warning Prefer offset instead of timezone
 * @param timezone - number | string - Offset(In millis): Ex. 7200000 | Timezone: Ex. 'Europe/Istanbul'
 * @return offset diff - number
 */
export const getTimezoneOffsetDiff = (timezone: string | number, date?: Date | number) => {
	const browserOffset = getTimezoneOffset(guessTimeZone(), date);

	return typeof timezone === 'number'
		? browserOffset - timezone
		: browserOffset - getTimezoneOffset(timezone, date);
};

/**
 * Formats offset to display in UI
 * @example ("Europe/Istanbul") => '+03:00'
 * @example (- 9 * 60 * 60 * 1000 - 45 * 60 * 1000) => '-09:45'
 * @param timezone - 'Europe/Istanbul' | number
 * @param date - Date | number
 * @return offset - '+03:00' | '-03:00'
 */
export const formatOffset = (timezone: string | number, date?: Date | number) => {
	const millis = typeof timezone === 'string' ? getTimezoneOffset(timezone, date) : timezone;

	const sign = millis >= 0 ? '+' : '-';
	const abs = Math.abs(millis);
	const hours = Math.floor(abs / HOUR_MILLIS);
	const minutes = Math.floor(abs / MINUTE_MILLIS) % HOUR_MINUTES;

	return (
		sign +
		Math.abs(hours).toString().padStart(2, '0') +
		':' +
		Math.abs(minutes).toString().padStart(2, '0')
	);
};

/**
 * Formats timezone to display in UI
 * @example ('Europe/Istanbul', 10800000) => 'Europe/Istanbul (+03:00)'
 * @example ('America/New_York', -18000000) => 'America/New York (-05:00)'
 * @param name - 'Europe/Istanbul' | 'Africa/Casablanca' | ...
 * @param offset - number - in millis - Ex: 86400000
 * @param variant - 'short' | 'long'
 * @return timezone - 'Europe/Istanbul (+03:00)' | 'Africa/Casablanca (UTC +00:00)' | ...
 */
export const formatTimezone = ({
	name,
	offset,
	variant,
}: {
	name: string;
	offset: number;
	variant: 'short' | 'long';
}) => {
	const timezone = name.replace(/_/g, ' ');
	const offsetString = formatOffset(offset);

	switch (variant) {
		case 'short':
			return `${timezone} (${offsetString})`;
		case 'long':
			return `${timezone} (UTC ${offsetString})`;
	}
};

/**
 * Formats epoch to ISO string in given offset
 * @param epoch - number - Represents time in milliseconds (In local timezone -> Not in Remote or UTC)
 * @param offset - number - Represents offset in milliseconds
 * @return time - string - ISO string. Ex: "2023-07-03T00:00+03:00"
 */
export const formatEpoch = (epoch: number, offset: number) => {
	return format(epoch, "yyyy-MM-dd'T'HH:mm") + formatOffset(offset);
};

/*
 * Formatting methods for local date without offset usage
 */
export const formatting = {
	/**
	 * Formats date to date hour minute string
	 * @param date - Date | number
	 * @return time - string - Ex. '2024/08/21 08:00'
	 */
	toDateHourMinute: (date: Date | number | string): string => {
		if (typeof date === 'string') {
			date = new Date(date);
		}

		return format(date, 'yyyy/MM/dd HH:mm');
	},
	/**
	 * Formats date to ISO string
	 * @param date - Date | number
	 * @return time - string - Ex. '2024-08-21T08:00:00+00:00'
	 */
	toISO: (date: Date | number | string): string => {
		if (typeof date === 'string') {
			date = new Date(date);
		}

		return formatISO(date);
	},
};

/**
 * Formats date to local timezone without changing nominal date. (Hacky)
 * @example Browser: UTC +3:00, Incoming: UTC +0:00 08:00 => UTC +3:00 08:00
 * @example Browser: UTC +7:00, Incoming: UTC -7:00 15:00 => UTC +7:00 15:00
 * @example Browser: UTC +3:00, Incoming: UTC +7:00 18:00 => UTC +3:00 18:00
 * @warning Prefer offset instead of timezone
 * @param epoch - number - In timezone
 * @param offset - number | string - Offset(In millis): Ex. 7200000 | Timezone: Ex. 'Europe/Istanbul'
 * @return time - If offset number ISO date string, else formatted date string
 */
export const formatInLocalTimezone = (epoch: number | Date, offset: string | number) => {
	if (typeof offset === 'number') {
		const diff = getTimezoneOffsetDiff(offset);

		return formatISO((typeof epoch === 'number' ? epoch : new Date(epoch).valueOf()) - diff);
	}

	return formatInTimeZone(utcToZonedTime(epoch, offset), guessTimeZone(), "yyyy-MM-dd'T'HH:mmxxx");
};

/**
 * Formats date to remote timezone without changing nominal date. (Hacky)
 * @example Browser: UTC +0:00, Incoming: UTC +0:00 08:00, Remote: UTC +5:00 => UTC +5:00 8:00
 * @example Browser: UTC -7:00, Incoming: UTC -7:00 15:00, Remote: UTC +0:00 => UTC +0:00 15:00
 * @example Browser: UTC +3:00, Incoming: UTC +3:00 15:00, Remote: UTC -9:00 => UTC -9:00 15:00
 * @warning Prefer offset instead of timezone
 * @param epoch - number - In timezone
 * @param timezone - string - Timezone: Ex. 'Europe/Istanbul'
 * @return time - Formatted date string
 */
export const formatInRemoteTimezone = (epoch: number | Date, timezone: string) => {
	return formatInTimeZone(zonedTimeToUtc(epoch, timezone), timezone, "yyyy-MM-dd'T'HH:mmxxx");
};

/**
 * Partition date into year, month, day, hour, minute
 */
export const partitionDate = (date: Date): DatePartition => {
	return {
		day: date.getDate(),
		hour: date.getHours(),
		minute: date.getMinutes(),
		month: date.getMonth(),
		year: date.getFullYear(),
	};
};

type DateParams = {
	year: number;
	month: number;
	day: number;
	hour?: number;
	minute?: number;
	millis?: number;
};

export const getDateInZone = (
	{ year, month, day, hour, minute, millis }: DateParams,
	timezone: string | number,
) => {
	const utc = Date.UTC(year, month, day, hour, minute, millis);
	const local = new Date(year, month, day, hour, minute, millis);

	if (typeof timezone === 'string') {
		return zonedTimeToUtc(local, timezone);
	}

	return new Date(utc - timezone);
};

// TODO: populerize the usage of this
/*
 * Converts date to back and forth from remote to local timezone without changing nominal date.
 * @warning Methods here should be used in conjunction with each other.
 */
export const conversion = {
	/**
	 * Formats date to local timezone without changing nominal date.
	 * @warning Date must be in utc
	 * @example Browser: UTC +3:00, Remote: UTC +0:00 8:00  => UTC +3:00 8:00
	 * @example Browser: UTC +7:00, Remote: UTC -7:00 15:00 => UTC +7:00 15:00
	 */
	toLocal: (date: Date | number, timezone: string | number) => {
		if (typeof timezone === 'string') {
			return utcToZonedTime(date, timezone);
		}

		return new Date(
			(typeof date === 'number' ? date : date.valueOf()) -
				(getTimezoneOffset(guessTimeZone(), date) - timezone),
		);
	},
	/**
	 * Formats date to remote timezone without changing nominal date.
	 * @warning Date must be in utc
	 * @example Browser: UTC +3:00 8:00, Remote: UTC +0:00 => UTC +0:00 8:00
	 * @example Browser: UTC +7:00 15:00, Remote: UTC -7:00 => UTC -7:00 15:00
	 */
	toRemote: (date: Date | number, timezone: string | number) => {
		if (typeof timezone === 'string') {
			return zonedTimeToUtc(date, timezone);
		}

		return new Date(
			(typeof date === 'number' ? date : date.valueOf()) +
				(getTimezoneOffset(guessTimeZone(), date) - timezone),
		);
	},
};

/**
 * Manipulates date by adding/subtracting days/weeks/months in given timezone
 * @param epoch - number | Date
 * @param timezone - string - Ex: 'Europe/Istanbul'
 * @returns date - Date
 */
export const zoned = {
	/** @description Set time to 12 o'clock in given timezone */
	midOfDay: (epoch: number | Date, timezone: string) => {
		const input = conversion.toLocal(epoch, timezone);

		const mid = getDateInZone(
			{
				year: input.getFullYear(),
				month: input.getMonth(),
				day: input.getDate(),
				hour: 12,
				minute: 0,
				millis: 0,
			},
			timezone,
		);

		return mid;
	},
	/** @description Add day in given timezone */
	nextDay: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const nextDay = addDays(input, 1);

		const output = conversion.toRemote(nextDay, timezone);

		return output;
	},
	/** @description Add week in given timezone */
	nextWeek: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const nextWeek = addWeeks(startOfWeek(input, { weekStartsOn: 1 }), 1);

		const output = conversion.toRemote(nextWeek, timezone);

		return output;
	},
	/** @description Add two weeks in given timezone */
	nextTwoWeeks: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const nextTwoWeeks = addWeeks(startOfWeek(input, { weekStartsOn: 1 }), 2);

		const output = conversion.toRemote(nextTwoWeeks, timezone);

		return output;
	},
	/** @description Add month in given timezone */
	nextMonth: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const nextMonth = addMonths(startOfMonth(input), 1);

		const output = conversion.toRemote(nextMonth, timezone);

		return output;
	},
	/** @description Subtract day in given timezone */
	previousDay: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const prevDay = subDays(input, 1);

		const output = conversion.toRemote(prevDay, timezone);

		return output;
	},
	/** @description Subtract week in given timezone */
	previousWeek: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const prevWeek = subWeeks(startOfWeek(input, { weekStartsOn: 1 }), 1);

		const output = conversion.toRemote(prevWeek, timezone);

		return output;
	},
	/** @description Subtract two weeks in given timezone */
	previousTwoWeeks: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const prevTwoWeeks = subWeeks(startOfWeek(input, { weekStartsOn: 1 }), 2);

		const output = conversion.toRemote(prevTwoWeeks, timezone);

		return output;
	},
	/** @description Subtract month in given timezone */
	previousMonth: (epoch: number | Date, timezone: string | number) => {
		const input = conversion.toLocal(epoch, timezone);

		const prevMonth = subMonths(startOfMonth(input), 1);

		const output = conversion.toRemote(prevMonth, timezone);

		return output;
	},
};
